<template>
  <div class="panel panel-default">
    <div class="panel-body">
      <div class="panel-title">
        {{ lang.get('judging.fast_start.scoring.new') }}
      </div>
      <validation-errors></validation-errors>
      <div class="form-group">
        <label for="label">
          {{ lang.get('scoring-criteria.form.label.label') }}
        </label>
        <multilingual
          mode="textarea"
          :resource="scoringCriterion"
          property="label"
          :supported-languages="supportedLanguages"
          @input="translated => onInput({ translated: translated })"
        ></multilingual>
      </div>
      <div :class="['form-group', { error: hasError('maximumScore') }]">
        <label for="maximumScore">
          {{ lang.get('scoring-criteria.form.maximum_score.label') }}
        </label>
        <input
          id="maximumScore"
          type="number"
          class="form-control"
          name="maximumScore"
          :value="scoringCriterion.maximumScore"
          @input="e => onInput({ maximumScore: parseInteger(e.target.value) })"
        />
      </div>
      <div :class="['form-group', { error: hasError('minimumScore') }]">
        <label for="minimumScore">
          {{ lang.get('scoring-criteria.form.minimum_score.label') }}
        </label>
        <input
          id="minimumScore"
          type="number"
          class="form-control"
          name="minimumScore"
          :value="scoringCriterion.minimumScore"
          @input="e => onInput({ minimumScore: parseInteger(e.target.value) })"
        />
      </div>
      <advanced-toggle v-model="advanced" />
      <div v-show="advanced">
        <div :class="['form-group', { error: hasError('scoringIncrement') }]">
          <label for="scoringIncrement">
            {{ lang.get('scoring-criteria.form.scoring_increment.label') }}
          </label>
          <input
            id="scoringIncrement"
            type="text"
            class="form-control"
            name="scoringIncrement"
            :value="scoringCriterion.scoringIncrement"
            @input="e => onInput({ scoringIncrement: e.target.value })"
          />
        </div>
        <div :class="['form-group', { error: hasError('weight') }]">
          <label for="weight">
            {{ lang.get('scoring-criteria.form.weight.label') }}
          </label>
          <help-icon :content="lang.get('scoring-criteria.form.weight.help')" />
          <input
            id="weight"
            type="number"
            class="form-control"
            name="weight"
            :value="scoringCriterion.weight"
            @input="e => onInput({ weight: e.target.value })"
          />
        </div>
      </div>
      <button class="btn btn-primary" :disabled="validating" @click="onSave">
        {{ lang.get('buttons.save') }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';
import buildFormData from '@/lib/formdata';
import { flipTranslations } from '@/lib/translations';
import { route, parseInteger } from '@/lib/utils.js';
import HelpIcon from '@/lib/components/Shared/HelpIcon';
import ValidationErrors from '@/lib/components/Shared/ValidationErrors';
import Multilingual from '@/lib/components/Translations/Multilingual';
import AdvancedToggle from '../AdvancedToggle';
import validateResourceMixin from '../validate-resource-mixin.js';

export default {
  inject: ['lang'],
  components: {
    HelpIcon,
    Multilingual,
    ValidationErrors,
    AdvancedToggle
  },
  mixins: [validateResourceMixin],
  props: {
    criterionId: {
      type: String,
      required: true
    },
    routes: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      advanced: false
    };
  },
  computed: {
    ...mapGetters('global', ['hasError']),
    ...mapState('global', ['supportedLanguages']),
    ...mapState('judgingFastStart', ['scoringCriteria']),
    scoringCriterion() {
      return this.scoringCriteria.find(c => c.id === this.criterionId);
    }
  },
  beforeDestroy() {
    this.resetValidationErrors();
  },
  methods: {
    ...mapMutations('judgingFastStart', ['updateScoringCriterion']),
    ...mapMutations('global', ['resetValidationErrors']),
    onInput(input) {
      this.updateScoringCriterion({ ...this.scoringCriterion, ...input });
    },
    validateCriterion() {
      // Build form data
      const data = buildFormData({
        ...this.scoringCriterion,
        translated: flipTranslations(this.scoringCriterion.translated)
      });

      // Validate scoring criterion
      return this.validateResource(
        route(this.routes['fast-start.scoring-criterion.validate']),
        data
      );
    },
    onSave() {
      this.validateCriterion().then(isValid => {
        if (isValid) {
          this.$emit('saved');
        }
      });
    },
    parseInteger
  }
};
</script>
